import React, { createContext, ComponentType, useContext } from 'react'
import { ProductLine } from '@adc/constants'

type Theme = {
  productLine: ProductLine
  version: 1 // we've decided to go with a simple numbering approach (not semver) to support future versioning rollout
}

const ApplicationThemeContext = createContext<Theme>({
  productLine: ProductLine.AuctionCom,
  version: 1,
})

const ApplicationThemeConsumer = ApplicationThemeContext.Consumer

interface ApplicationThemeProviderProps {
  children: React.ReactNode
  theme: Theme
}

const ApplicationThemeProvider = ({
  children,
  theme = {
    productLine: ProductLine.AuctionCom,
    version: 1,
  },
}: ApplicationThemeProviderProps) => {
  return (
    <ApplicationThemeContext.Provider value={theme}>
      {children}
    </ApplicationThemeContext.Provider>
  )
}

const useApplicationTheme = () => {
  const context = useContext(ApplicationThemeContext)
  return context
}

interface WithApplicationThemeProps {
  theme: Theme
}

const withApplicationTheme =
  <P extends object>(Component: ComponentType<P & WithApplicationThemeProps>) =>
  (props: Omit<P, keyof WithApplicationThemeProps>) =>
    (
      <ApplicationThemeConsumer>
        {theme => <Component theme={theme} {...(props as P)} />}
      </ApplicationThemeConsumer>
    )

export {
  ApplicationThemeProvider,
  ApplicationThemeConsumer,
  useApplicationTheme,
  withApplicationTheme,
  ProductLine,
  Theme,
}
